import { FC, Fragment, useContext, useState } from "react";
import Modal from "react-modal";
import styled from "styled-components";
import { CancelIcon, ClockForwardIcon } from "../components/Icons";
import "./customModal.css";
import ThemeContext from "../themes/context";
import cancelSchedule from "../api/cancelSchedule";
import { useNavigate } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";
import { showToast } from "../utils/showToast";

const ModalContentWrapper = styled.div`
	background: #ffffff;
	box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
	border-radius: 10px;
	position: absolute;
	left: 40px;
	right: 40px;
	top: 40px;
	background: #ffffff;
	overflow: auto;
	-webkit-overflow-scrolling: touch;
	outline: none;
	padding: 15px;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: stretch;
	align-content: flex-start;
`;

const ModalTitle = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	line-height: 165.5%;
	text-align: center;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	text-align: center;
	padding: 10px;
`;

const ModalButton = styled.div`
	background: ${props => props.theme.colors.lightGrayVitat || "#eadced"};
	box-shadow: 0px 4px 39px 6px rgba(246, 242, 252, 0.59);
	border-radius: 7px;
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	align-content: stretch;
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 165.5%;
	text-align: center;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || props.theme.colors.secondary};
	padding-left: 25px;
	padding-right: 25px;
	padding-top: 10px;
	padding-bottom: 10px;
	gap: 10px;
	height: 70px;
	width: 50%;
`;

const ModalButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	align-content: stretch;
	gap: 8px;
	margin-top: 50px;
	margin-bottom: 25px;
`;

interface CancelSchedulingModalProps {
	isOpen: boolean;
	schedulingId: string;
	scheduleProcedureId: string;
	professionalId: string;
	handleClose: (cenceled: boolean) => void;
}

/**
 * ### Modal de cancelamento do agendamnto
 * Mostra um dialogo pedindo se o usuario gostaria de cancelar ou adiar o agendamento.
 */
const CancelSchedulingModal: FC<CancelSchedulingModalProps> = ({ isOpen, handleClose, schedulingId, professionalId, scheduleProcedureId }) => {
	const theme = useContext(ThemeContext);
	const navigate = useNavigate();
	const [isLoadingCancel, setIsLoadingCancel] = useState<boolean>(false);

	const handleCancelSchedule = async () => {
		if (!schedulingId) return;

		setIsLoadingCancel(true);
		try {
			const response = await cancelSchedule(schedulingId);
			if (response?.success === false) {
				if (response.status === 403) {
					const minCancelTime = JSON.parse(response.message).minCancelTime;
					showToast("error", {
						text: `O cancelamento só é permitido com no mínimo ${minCancelTime} minutos de antecedência!`,
					});
					return;
				}
				showToast("error", {
					text: "Não foi possível executar o cancelamento, tente novamente mais tarde.",
				});
				return;
			}

			handleClose(true);
			sessionStorage.setItem("status", "canceled");
			navigate("/success", { replace: true });
		} catch (error) {
			showToast("error", {
				text: "Ocorreu um erro ao cancelar o agendamento, tente novamente mais tarde.",
				showConfirmButton: true,
				confirmButtonText: "Ok",
			});
		} finally {
			setIsLoadingCancel(false);
		}
	};

	const handleReschedule = () => {
		navigate(`/medicos/${professionalId}/agendamentos/${schedulingId}/reschedule?procedure=${scheduleProcedureId}`);
		handleClose(false);
	};

	return (
		<Modal
			ariaHideApp={false}
			isOpen={isOpen}
			onRequestClose={() => handleClose(false)}
			overlayClassName="modal-overlay-personalizado"
			contentElement={(props, children) => <ModalContentWrapper onClick={e => e.stopPropagation()}>{children}</ModalContentWrapper>}
		>
			<ModalTitle>Você deseja cancelar ou reagendar sua consulta?</ModalTitle>
			<ModalButtonWrapper>
				<ModalButton onClick={handleCancelSchedule}>
					{isLoadingCancel ? (
						<PulseLoader color={"#FFFFFF"} loading size={8} />
					) : (
						<Fragment>
							<CancelIcon color={theme.colors.blackVitat || theme.colors.secondary} size={20} />
							<b>Cancelar agendamento</b>
						</Fragment>
					)}
				</ModalButton>
				<ModalButton onClick={handleReschedule}>
					<ClockForwardIcon color={theme.colors.blackVitat || theme.colors.secondary} size={20} />
					<b>Reagendar</b>
				</ModalButton>
			</ModalButtonWrapper>
		</Modal>
	);
};

export default CancelSchedulingModal;
